import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { lp } from "../components/Navbar";
import { MaskedLine, Reveal, Kicker } from "../components/motion";

export function PageHero({ kicker, title, copy, children }) {
  return (
    <section className="relative overflow-hidden border-b border-esaia-line bg-white hairline-grid">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
        <Reveal y={12}>
          <Kicker>{kicker}</Kicker>
        </Reveal>
        <h1 className="mt-5 max-w-4xl text-4xl font-semibold tracking-tight text-esaia-ink sm:text-5xl">
          <MaskedLine delay={0.1}>{title}</MaskedLine>
        </h1>
        {copy && (
          <Reveal delay={0.25} className="mt-6 max-w-2xl text-base leading-relaxed text-esaia-muted">
            {copy}
          </Reveal>
        )}
        {children}
      </div>
    </section>
  );
}

export function CTABand({ title, copy, primary, secondary }) {
  const { lang } = useLanguage();
  return (
    <section className="relative overflow-hidden bg-esaia-blue" data-testid="cta-band">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />
      <svg className="absolute -right-12 -top-12 opacity-25" width="240" height="240" viewBox="0 0 96 96" aria-hidden="true">
        <path d="M48 4 92 48 48 92 4 48Z" fill="none" stroke="#ffffff" strokeWidth="0.8" />
        <path d="M48 26 70 48 48 70 26 48Z" fill="#ffffff" opacity="0.3" />
      </svg>
      <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20">
        <Reveal className="max-w-2xl">
          <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">{title}</h2>
          {copy && <p className="mt-4 text-sm leading-relaxed text-blue-100">{copy}</p>}
        </Reveal>
        <Reveal delay={0.15} className="flex flex-col gap-3 sm:flex-row">
          {primary && (
            <Link
              to={lp(lang, primary.to)}
              data-testid={primary.testid || "cta-band-primary"}
              className="group inline-flex items-center justify-center gap-2 bg-white px-6 py-3 text-sm font-semibold text-esaia-blue transition-colors hover:bg-blue-50"
            >
              {primary.label}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          )}
          {secondary && (
            <Link
              to={lp(lang, secondary.to)}
              data-testid={secondary.testid || "cta-band-secondary"}
              className="inline-flex items-center justify-center gap-2 border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-esaia-blue"
            >
              {secondary.label}
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export function DisclaimerNote() {
  const { t } = useLanguage();
  return (
    <p className="mt-8 max-w-4xl border-l-2 border-esaia-blue/40 pl-4 text-xs leading-relaxed text-esaia-subtle" data-testid="certification-disclaimer-note">
      {t.footer.disclaimer}
    </p>
  );
}
